import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Stack, Skeleton, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { dailyFocusBlocksApi, preferencesApi } from '../../api/services';
import type { DailyFocusBlock } from '../../types/models';
import { formatMinutes } from '../../utils/dashboard';
import { focusTimeCopy } from '../../copy/focusTime';

// ---------------------------------------------------------------------------
// WeeklyFocusKpi — the single number that reinforces the focus-time promise
// every time the user opens the dashboard. Hours protected this week versus
// target, plus a thin proportional bar in the same language as CapacityStrip.
//
// Self-fetches so the host page does not need to change its data-loading.
// Two requests (focus blocks for the week + preferences) run in parallel.
// Cached in component state for the lifetime of the mount; the briefing
// dashboard does not remount on normal navigation inside the app so this is
// effectively a one-shot load.
// ---------------------------------------------------------------------------

function startOfWeek(d: Date): Date {
  // Monday as the start of the week. Consistent with FocusTimePage.
  const date = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = date.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  date.setDate(date.getDate() + diff);
  return date;
}

function toIsoDate(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function blockMinutes(b: DailyFocusBlock): number {
  const [sh, sm] = b.startTime.split(':').map(Number);
  const [eh, em] = b.endTime.split(':').map(Number);
  return Math.max(0, eh * 60 + em - (sh * 60 + sm));
}

export function WeeklyFocusKpi() {
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState<DailyFocusBlock[] | null>(null);
  const [targetDailyMinutes, setTargetDailyMinutes] = useState<number | null>(null);
  const [error, setError] = useState<boolean>(false);

  const { monday, sunday } = useMemo(() => {
    const now = new Date();
    const m = startOfWeek(now);
    const s = new Date(m);
    s.setDate(s.getDate() + 6);
    return { monday: toIsoDate(m), sunday: toIsoDate(s) };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      dailyFocusBlocksApi.getByRange(monday, sunday).catch(() => null),
      preferencesApi.get().catch(() => null),
    ])
      .then(([bs, prefs]) => {
        if (cancelled) return;
        if (bs === null || prefs === null) {
          setError(true);
          return;
        }
        setBlocks(bs);
        setTargetDailyMinutes(prefs.idealDailyFocusMinutes ?? 0);
      });
    return () => {
      cancelled = true;
    };
  }, [monday, sunday]);

  // Derived metrics. `protected` is the total minutes of focus blocks this
  // week; `target` is weekday-only (5 days) since that is what the product
  // actually protects. Ratio is clamped to [0, 1] for the bar.
  const protectedMinutes = useMemo(
    () => (blocks ? blocks.reduce((sum, b) => sum + blockMinutes(b), 0) : 0),
    [blocks],
  );
  const targetWeeklyMinutes = (targetDailyMinutes ?? 0) * 5;
  const ratio = targetWeeklyMinutes > 0 ? Math.min(1, protectedMinutes / targetWeeklyMinutes) : 0;

  // Severity colour. Hits the theme palette so dark / light / high-contrast
  // all resolve correctly. Target >= 100% → on plan; >= 70% → at risk;
  // below that → breach.
  let barColour: 'status.onPlan' | 'status.atRisk' | 'status.breach' = 'status.onPlan';
  if (targetWeeklyMinutes === 0) {
    barColour = 'status.atRisk'; // No target configured is itself a gap.
  } else if (ratio < 0.7) {
    barColour = 'status.breach';
  } else if (ratio < 1) {
    barColour = 'status.atRisk';
  }

  const loading = blocks === null && !error;

  const tooltipLabel =
    targetWeeklyMinutes === 0
      ? 'Set an ideal daily focus target in Preferences to calibrate this metric.'
      : `${formatMinutes(protectedMinutes)} protected of ${formatMinutes(targetWeeklyMinutes)} target this week.`;

  return (
    <Tooltip title={tooltipLabel} arrow placement="top">
      <Box
        role="button"
        tabIndex={0}
        onClick={() => navigate('/focus')}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate('/focus');
          }
        }}
        sx={{
          px: 2.5,
          py: 1.75,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          cursor: 'pointer',
          transition: 'border-color 120ms',
          '&:hover': { borderColor: 'primary.main' },
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            letterSpacing: '0.12em',
            fontWeight: 600,
            display: 'block',
          }}
        >
          {focusTimeCopy.kpiLabel}
        </Typography>

        {loading ? (
          <Skeleton variant="text" width={120} height={36} sx={{ mt: 0.5 }} />
        ) : (
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.25 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.02em',
              }}
            >
              {formatMinutes(protectedMinutes)}
            </Typography>
            {targetWeeklyMinutes > 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                of {formatMinutes(targetWeeklyMinutes)} {focusTimeCopy.kpiVsTarget}
              </Typography>
            )}
          </Stack>
        )}

        {/* Proportional bar. Always present so the layout is stable between
            loading and loaded; empty fills read as "zero progress". */}
        <Box
          sx={{
            mt: 1.25,
            height: 6,
            borderRadius: 999,
            overflow: 'hidden',
            bgcolor: 'action.hover',
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: `${ratio * 100}%`,
              height: '100%',
              bgcolor: barColour,
              transition: 'width 200ms',
            }}
          />
        </Box>
      </Box>
    </Tooltip>
  );
}

export default WeeklyFocusKpi;
